// ============================================================
// multiplayer/client.ts — WebSocket client for code-based 2-4 player rooms
// "Stairs?! NOOOOOOO!" — Claptrap (but ranked ladders go UP)
// ============================================================

import type { InterferenceEffect, MultiplayerPeer, MultiplayerState } from '../game/types';

export type RoomError =
  | 'invalid_code'
  | 'room_not_found'
  | 'room_full'
  | 'room_already_started'
  | 'already_in_room'
  | 'not_host'
  | 'need_two_players'
  | 'no_codes_available';

export type MultiplayerEvent =
  | { type: 'connected' }
  | { type: 'roomCreated'; code: string; roomId: string; playerId: string; players: MultiplayerPeer[] }
  | { type: 'roomJoined'; code: string; roomId: string; playerId: string; isHost: boolean; players: MultiplayerPeer[] }
  | { type: 'peerJoined'; player: { id: string; name: string }; players: MultiplayerPeer[] }
  | { type: 'peerLeft'; playerId: string; players: MultiplayerPeer[]; hostId: string }
  | { type: 'gameStarted'; seed: number; players: MultiplayerPeer[]; difficulty: string; gameMode: 'normal' | 'miroir' }
  | { type: 'peerScore'; playerId: string; score: number; level: number }
  | { type: 'interference'; from: string; fromId: string; effect: InterferenceEffect }
  | { type: 'peerGameOver'; playerId: string; finalScore: number }
  | { type: 'chat'; text: string; from: string; fromId: string; ts: number }
  | { type: 'bsRequest'; from: string; fromId: string; requestId: string }
  | { type: 'bsSnapshot'; from: string; fromId: string; requestId: string; snapshot: unknown }
  | { type: 'wordAccepted'; requestId: string; claimantId: string; claimantName: string; ballIds: number[]; word: string; points: number }
  | { type: 'wordRejected'; requestId: string; reason: string }
  | { type: 'roomError'; message: RoomError | string }
  | { type: 'disconnected' }
  | { type: 'error'; message: string };

type EventHandler = (event: MultiplayerEvent) => void;

const WS_BASE = 'wss://wardogs-api.apexdiligence.workers.dev/ws';

let ws: WebSocket | null = null;
let handler: EventHandler | null = null;
let state: MultiplayerState = freshState();

function freshState(): MultiplayerState {
  return {
    roomId: '',
    code: '',
    playerId: '',
    isHost: false,
    connected: false,
    peers: {},
  };
}

function rosterToMap(roster: Array<{ id: string; name: string; isHost: boolean }>): Record<string, MultiplayerPeer> {
  const map: Record<string, MultiplayerPeer> = {};
  for (const p of roster) {
    const prev = state.peers[p.id];
    map[p.id] = {
      id: p.id,
      name: p.name,
      isHost: p.isHost,
      score: prev?.score ?? 0,
      level: prev?.level ?? 1,
      finished: prev?.finished ?? false,
    };
  }
  return map;
}

export function getMultiplayerState(): MultiplayerState {
  return state;
}

export function connectMultiplayer(onEvent: EventHandler, token?: string, displayName?: string) {
  handler = onEvent;
  // Disconnect any prior socket so callers can call connectMultiplayer freely.
  if (ws && ws.readyState !== WebSocket.CLOSED) {
    try { ws.close(); } catch { /* ignore */ }
  }
  state = freshState();

  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (displayName) params.set('name', displayName);
  const qs = params.toString();
  const url = qs ? `${WS_BASE}?${qs}` : WS_BASE;

  ws = new WebSocket(url);

  ws.onopen = () => {
    state.connected = true;
    handler?.({ type: 'connected' });
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data as string);
      handleMessage(msg);
    } catch (e) {
      console.warn('WS parse error:', e);
    }
  };

  ws.onclose = () => {
    state.connected = false;
    handler?.({ type: 'disconnected' });
  };

  ws.onerror = () => {
    handler?.({ type: 'error', message: 'Connection failed' });
  };
}

export function disconnectMultiplayer() {
  if (ws) { try { ws.close(); } catch { /* ignore */ } ws = null; }
  state = freshState();
}

export function createRoom() {
  send({ type: 'createRoom' });
}

export function joinRoom(code: string) {
  const cleaned = code.trim().toUpperCase();
  send({ type: 'joinRoom', code: cleaned });
}

export function leaveRoom() {
  send({ type: 'leaveRoom' });
}

export function startGame(opts?: { difficulty?: string; gameMode?: 'normal' | 'miroir' }) {
  send({ type: 'startGame', difficulty: opts?.difficulty, gameMode: opts?.gameMode });
}

export function sendScoreUpdate(score: number, level: number) {
  send({ type: 'scoreUpdate', score, level });
}

export function sendInterference(effectType: string) {
  send({ type: 'interference', effectType, duration: 5000 });
}

export function sendGameOver(finalScore: number) {
  send({ type: 'gameOver', finalScore });
}

export function sendChat(text: string) {
  const trimmed = text.trim().slice(0, 200);
  if (!trimmed) return;
  send({ type: 'chat', text: trimmed });
}

// Ask every other peer in the room for a debug snapshot.
export function sendBsRequest(requestId: string) {
  send({ type: 'bsRequest', requestId });
}

// Respond to a bsRequest with our own snapshot, routed to the requester.
export function sendBsSnapshot(requestId: string, toId: string, snapshot: unknown) {
  send({ type: 'bsSnapshot', requestId, toId, snapshot });
}

// Miroir: request the worker to accept a word claim against specific ball IDs.
// First claim wins; loser receives wordRejected.
export function sendWordClaim(requestId: string, ballIds: number[], word: string, points: number) {
  send({ type: 'wordClaim', requestId, ballIds, word, points });
}

// Replace the active event handler. Used when ownership of the WebSocket moves
// from the lobby screen to the in-game screen so chat keeps flowing.
export function setMultiplayerHandler(fn: EventHandler) { handler = fn; }

export function isMultiplayerConnected(): boolean {
  return ws?.readyState === WebSocket.OPEN;
}

function send(data: unknown) {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(data));
  }
}

function handleMessage(msg: Record<string, unknown>) {
  switch (msg.type) {
    case 'roomCreated': {
      state.code = (msg.code as string) || '';
      state.roomId = (msg.roomId as string) || '';
      state.playerId = (msg.playerId as string) || '';
      state.isHost = true;
      state.peers = rosterToMap((msg.players as Array<{ id: string; name: string; isHost: boolean }>) || []);
      handler?.({
        type: 'roomCreated',
        code: state.code,
        roomId: state.roomId,
        playerId: state.playerId,
        players: Object.values(state.peers),
      });
      break;
    }
    case 'roomJoined': {
      state.code = (msg.code as string) || '';
      state.roomId = (msg.roomId as string) || '';
      state.playerId = (msg.playerId as string) || '';
      state.isHost = Boolean(msg.isHost);
      state.peers = rosterToMap((msg.players as Array<{ id: string; name: string; isHost: boolean }>) || []);
      handler?.({
        type: 'roomJoined',
        code: state.code,
        roomId: state.roomId,
        playerId: state.playerId,
        isHost: state.isHost,
        players: Object.values(state.peers),
      });
      break;
    }
    case 'peerJoined': {
      state.peers = rosterToMap((msg.players as Array<{ id: string; name: string; isHost: boolean }>) || []);
      const player = msg.player as { id: string; name: string };
      handler?.({
        type: 'peerJoined',
        player,
        players: Object.values(state.peers),
      });
      break;
    }
    case 'peerLeft': {
      const playerId = (msg.playerId as string) || '';
      const hostId = (msg.hostId as string) || '';
      state.peers = rosterToMap((msg.players as Array<{ id: string; name: string; isHost: boolean }>) || []);
      if (hostId === state.playerId) state.isHost = true;
      handler?.({
        type: 'peerLeft',
        playerId,
        hostId,
        players: Object.values(state.peers),
      });
      break;
    }
    case 'gameStarted': {
      const seed = (msg.seed as number) || 0;
      const difficulty = (msg.difficulty as string) || 'normal';
      const gameMode = (msg.gameMode as 'normal' | 'miroir') || 'normal';
      state.peers = rosterToMap((msg.players as Array<{ id: string; name: string; isHost: boolean }>) || Object.values(state.peers).map(p => ({ id: p.id, name: p.name, isHost: p.isHost })));
      handler?.({ type: 'gameStarted', seed, players: Object.values(state.peers), difficulty, gameMode });
      break;
    }
    case 'peerScore': {
      const playerId = (msg.playerId as string) || '';
      const score = (msg.score as number) || 0;
      const level = (msg.level as number) || 1;
      const peer = state.peers[playerId];
      if (peer) {
        peer.score = score;
        peer.level = level;
      }
      handler?.({ type: 'peerScore', playerId, score, level });
      break;
    }
    case 'interference': {
      handler?.({
        type: 'interference',
        from: (msg.from as string) || '?',
        fromId: (msg.fromId as string) || '',
        effect: {
          type: msg.effectType as InterferenceEffect['type'],
          duration: (msg.duration as number) || 5000,
          timestamp: Date.now(),
        },
      });
      break;
    }
    case 'peerGameOver': {
      const playerId = (msg.playerId as string) || '';
      const finalScore = (msg.finalScore as number) || 0;
      const peer = state.peers[playerId];
      if (peer) {
        peer.finished = true;
        peer.score = finalScore;
      }
      handler?.({ type: 'peerGameOver', playerId, finalScore });
      break;
    }
    case 'chat': {
      handler?.({
        type: 'chat',
        text: (msg.text as string) || '',
        from: (msg.from as string) || '?',
        fromId: (msg.fromId as string) || '',
        ts: (msg.ts as number) || Date.now(),
      });
      break;
    }
    case 'bsRequest': {
      handler?.({
        type: 'bsRequest',
        from: (msg.from as string) || '?',
        fromId: (msg.fromId as string) || '',
        requestId: (msg.requestId as string) || '',
      });
      break;
    }
    case 'bsSnapshot': {
      handler?.({
        type: 'bsSnapshot',
        from: (msg.from as string) || '?',
        fromId: (msg.fromId as string) || '',
        requestId: (msg.requestId as string) || '',
        snapshot: msg.snapshot ?? null,
      });
      break;
    }
    case 'wordAccepted': {
      handler?.({
        type: 'wordAccepted',
        requestId: (msg.requestId as string) || '',
        claimantId: (msg.claimantId as string) || '',
        claimantName: (msg.claimantName as string) || '?',
        ballIds: Array.isArray(msg.ballIds) ? (msg.ballIds as number[]) : [],
        word: (msg.word as string) || '',
        points: (msg.points as number) || 0,
      });
      break;
    }
    case 'wordRejected': {
      handler?.({
        type: 'wordRejected',
        requestId: (msg.requestId as string) || '',
        reason: (msg.reason as string) || 'unknown',
      });
      break;
    }
    case 'roomError': {
      handler?.({
        type: 'roomError',
        message: (msg.message as string) || 'unknown',
      });
      break;
    }
    default:
      console.log('Unknown WS message:', msg);
  }
}
